import React from 'react';
import { designTokens } from '../../theme/designTokens';

export interface PageHeaderProps {
  /**
   * Page title
   */
  title: string;

  /**
   * Subtitle/description
   */
  subtitle?: string;

  /**
   * Action buttons/elements (displayed on the right)
   */
  actions?: React.ReactNode;

  /**
   * Breadcrumb/back navigation
   */
  breadcrumb?: React.ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * PageHeader Component
 *
 * Standardized page header with title, subtitle, and actions.
 * Provides consistent spacing and layout across all pages.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Projects"
 *   subtitle="Manage all your projects"
 *   actions={<Button>New Project</Button>}
 * />
 *
 * <PageHeader
 *   title="Project Detail"
 *   breadcrumb={<Link to="/projects">← Back to Projects</Link>}
 *   actions={
 *     <>
 *       <Button variant="secondary">Edit</Button>
 *       <Button variant="danger">Delete</Button>
 *     </>
 *   }
 * />
 * ```
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  breadcrumb,
  className = '',
}) => {
  return (
    <header className={`bg-white border border-gray-200 rounded-lg mx-6 mt-6 ${className}`}>
      <div className="px-6 py-4">
        {/* Breadcrumb/Back Navigation */}
        {breadcrumb && (
          <div className="mb-3">
            {breadcrumb}
          </div>
        )}

        {/* Title & Actions Row */}
        <div className="flex items-start justify-between">
          {/* Title & Subtitle */}
          <div className="flex-1">
            <h1 className={designTokens.typography.h1.className}>
              {title}
            </h1>
            {subtitle && (
              <p className={`${designTokens.typography.small.className} mt-1`}>
                {subtitle}
              </p>
            )}
          </div>

          {/* Actions */}
          {actions && (
            <div className="flex-shrink-0 ml-4 flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
